import type { LoginInput, MyInfo } from "../../types/auth.types";
import { apiClient } from "../axios.config";
import type {
  ConfirmOTPRequest,
  JoinConfirmOTPRequest,
  JoinOTPRequest,
  OTPRequest,
  UpdateMarketingAgreeRequest,
  UpdateRegionRequest,
} from "../dto/request/auth.request";
import type {
  AccessResponse,
  AuthCertificationResponse,
  JoinTermsResponse,
} from "../dto/response/auth.response";
import type { Response } from "../dto/response/common.response";

const model = "/v1/auth";

/**
 * Auth Service API
 * @module AuthService
 * @description 회원가입/로그인 등 인증 관련 API
 * @author DongGyun Yoo
 * @since 1.0.0
 */

export async function joinRequestOtp(
  form: JoinOTPRequest,
): Promise<Response<AuthCertificationResponse>> {
  const { data } = await apiClient.post(`${model}/join/request-otp`, form);
  return data;
}

export async function joinConfirmOtp(
  form: JoinConfirmOTPRequest,
): Promise<Response<AccessResponse>> {
  const { data } = await apiClient.post(`${model}/join/confirm-otp`, form);
  return data;
}

export async function joinTermList(): Promise<
  Response<{ termsList: JoinTermsResponse[] }>
> {
  const { data } = await apiClient.get(`${model}/join/terms-list`);
  return data;
}

export async function joinTermNoticeDates(
  type: string,
): Promise<Response<string[]>> {
  const { data } = await apiClient.get(`${model}/join/term/notice-dates`, {
    params: { type },
  });
  return data;
}

export async function joinTerm(
  type: string,
  noticeDate: string,
): Promise<Response<JoinTermsResponse>> {
  const { data } = await apiClient.get(`${model}/join/term`, {
    params: { type, noticeDate },
  });
  return data;
}

export async function requestOtp(form: OTPRequest): Promise<Response> {
  const { data } = await apiClient.post(`${model}/request-otp`, form);
  return data;
}

export async function loginConfirmOtp(
  form: LoginInput,
): Promise<Response<AccessResponse>> {
  const { data } = await apiClient.post(`${model}/login/confirm-otp`, form);
  return data;
}

export async function updateRequestOtp(
  form: JoinOTPRequest,
): Promise<Response<AuthCertificationResponse>> {
  const { data } = await apiClient.post(`${model}/update-me/request-otp`, form);
  return data;
}

export async function updateConfirmOtp(
  form: ConfirmOTPRequest,
): Promise<Response<AccessResponse>> {
  const { data } = await apiClient.put(`${model}/update-me/confirm-otp`, form);
  return data;
}

/** 토큰 갱신만 별도 timeout (전역 30초보다 김). 3G 등 느린 망에서 갱신 실패로 로그아웃되는 것을 완화 */
export const TOKEN_REFRESH_TIMEOUT_MS = 180_000;

export async function refreshToken(
  token: string,
): Promise<Response<AccessResponse>> {
  const { data } = await apiClient.post(
    `${model}/refresh-token`,
    {
      refreshToken: token,
    },
    { timeout: TOKEN_REFRESH_TIMEOUT_MS },
  );
  return data;
}

export async function getMyInfo(): Promise<Response<MyInfo>> {
  const { data } = await apiClient.get(`${model}/my-info`);
  return data;
}

export async function withdraw(form: LoginInput): Promise<Response> {
  const { data } = await apiClient.put(`${model}/withdraw`, form);
  return data;
}

export async function fcmTokenUpdate({
  deviceType,
  fcmToken,
}: {
  deviceType: string;
  fcmToken: string;
}) {
  const { data } = await apiClient.post(`${model}/fcm-token`, {
    deviceType,
    fcmToken,
  });
  return data;
}

export async function emailUpdate(email: string): Promise<Response> {
  const { data } = await apiClient.put(`${model}/update-me/email`, {
    email: email,
  });
  return data;
}

export async function updateMarketingAgree(
  form: UpdateMarketingAgreeRequest,
): Promise<Response<string>> {
  const { data } = await apiClient.put(
    `${model}/update-me/marketing-agree`,
    form,
  );
  return data;
}

export async function updateRegion(
  form: UpdateRegionRequest,
): Promise<Response<string>> {
  const { data } = await apiClient.put(`${model}/update-me/region`, form);
  return data;
}

export async function updateNickname(
  nickname: string,
): Promise<Response<string>> {
  const { data } = await apiClient.put(`${model}/update-me/nickname`, {
    nickname,
  });
  return data;
}

export async function logout(refreshToken: string): Promise<Response> {
  const { data } = await apiClient.put(`${model}/logout`, {
    refreshToken: refreshToken,
  });
  return data;
}

export async function fcmToken(form: {
  deviceType: string;
  fcmToken: string;
}): Promise<Response> {
  const { data } = await apiClient.put(`${model}/fcm-token`, form);
  return data;
}
